import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { v4 as uuidv4 } from "uuid";
import Autocomplete from "./Autocomplete";
import Question, { Optional } from "./Question";
import Label from "./Label";
import Hint from "./Hint";
import ErrorMessage from "./ErrorMessage";
import { createDataTestId } from "./utils/helpers";

/**
 * Autocomplete component wrapped with label, hint, error, and validation support.
 *
 * This wrapper adds form-field styling and behavior to the generic Autocomplete component.
 *
 * Value handling:
 * - Accepts `value` as a string (e.g., "GBR", "USA")
 * - Converts it to an option object for Autocomplete
 * - Converts back to string in onChange callback
 *
 * Event handlers:
 * - Uses slotProps to pass custom handlers (onBlur, aria attributes)
 */
function AutocompleteQuestion({
  id,
  options,
  value,
  onChange,
  placeholder,
  question,
  hint,
  optional,
  validationError,
  validate,
  disabled,
  readOnly,
  autoHighlight,
  sortOptions,
  disableClearable,
  noOptionsText,
  filterKeys,
  ...otherProps
}) {
  const generatedIdRef = React.useRef(null);
  if (!generatedIdRef.current) {
    generatedIdRef.current = `autocomplete-${uuidv4()}`;
  }
  const inputId = id || generatedIdRef.current;

  // Generate data-testid based on question text if provided
  const dataTestId = question ? createDataTestId("autocomplete", question, null) : undefined;

  // Convert string value to option object for Autocomplete
  const acValue = useMemo(() => {
    return options.find((option) => option.value === value);
  }, [options, value]);

  // Convert from option object back to value string
  const handleChange = React.useCallback(
    (event, newValue) => {
      if (newValue == null) {
        onChange("");
      } else {
        onChange(newValue.value);
      }
    },
    [onChange],
  );

  // Build slotProps for validation handlers and aria attributes
  const slotProps = React.useMemo(() => {
    const errorId = validationError ? `${inputId}-error` : undefined;

    const inputProps = {
      "aria-describedby": errorId,
      "aria-invalid": validationError ? "true" : undefined,
    };

    if (dataTestId) {
      inputProps["data-testid"] = dataTestId;
    }

    if (validate) {
      inputProps.onBlur = validate;
      inputProps.onFocus = validate;
    }

    return {
      input: inputProps,
    };
  }, [validate, validationError, inputId, dataTestId]);

  return (
    <Question>
      {question && (
        <Label htmlFor={inputId}>
          {question} {optional ? <Optional /> : null}
          {hint ? <Hint>{hint}</Hint> : null}
          {validationError ? <ErrorMessage>{validationError}</ErrorMessage> : null}
        </Label>
      )}
      <Autocomplete
        id={inputId}
        options={options}
        value={acValue || null}
        onChange={handleChange}
        placeholder={placeholder}
        disabled={disabled}
        readOnly={readOnly}
        autoHighlight={autoHighlight}
        sortOptions={sortOptions}
        filterKeys={filterKeys}
        disableClearable={disableClearable}
        noOptionsText={noOptionsText}
        error={!!validationError}
        slotProps={slotProps}
        {...otherProps}
      />
    </Question>
  );
}

AutocompleteQuestion.propTypes = {
  /**
   * HTML id attribute for the input element (generated if not provided).
   */
  id: PropTypes.string,
  /**
   * Array of options to display in the dropdown.
   */
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
      emoji: PropTypes.string,
    }),
  ).isRequired,
  /**
   * Currently selected value.
   */
  value: PropTypes.string,
  /**
   * Callback fired when the value changes.
   */
  onChange: PropTypes.func.isRequired,
  /**
   * Placeholder text for the input.
   */
  placeholder: PropTypes.string,
  /**
   * Label text for the field.
   */
  question: PropTypes.string,
  /**
   * Hint text displayed below the label.
   */
  hint: PropTypes.string,
  /**
   * Whether the field is optional.
   */
  optional: PropTypes.bool,
  /**
   * Validation error message to display.
   */
  validationError: PropTypes.string,
  /**
   * Validation callback triggered on blur.
   */
  validate: PropTypes.func,
  /**
   * Whether the field is disabled.
   */
  disabled: PropTypes.bool,
  /**
   * Whether the field is readonly.
   */
  readOnly: PropTypes.bool,
  /**
   * If `true`, the first option is automatically highlighted.
   */
  autoHighlight: PropTypes.bool,
  /**
   * Function to apply custom sorting instead of default alphabetical.
   */
  sortOptions: PropTypes.func,
  /**
   * Function to filter options. If not provided, uses default filtering with filterKeys.
   */
  filterOptions: PropTypes.func,
  /**
   * If `true`, the input can't be cleared.
   */
  disableClearable: PropTypes.bool,
  /**
   * Text to display when no options match.
   */
  noOptionsText: PropTypes.string,
  /**
   * Keys to use for filtering (defaults to ['label']).
   * Pass ['label', 'aliases'] to search through aliases.
   */
  filterKeys: PropTypes.arrayOf(PropTypes.string),
};

export default AutocompleteQuestion;
